import React, { useState } from "react";
import styled from "styled-components";
import { Dropdown } from "..";
import { generateDays, generateMonths, generateYears } from "./dateUtils";
import { DropdownStyles } from "../../types/styles";
import { useTranslation } from "react-i18next";

const DateOfBirthContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  font-size: 13px;
`;

const DateOfBirthLabel = styled.label<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.OverallGeneralTextColour};
  font-weight: 700;
  font-size: 13px;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type DateOfBirthProps = {
  dateOfBirth?: Date | null;
  setDateOfBirth: React.Dispatch<React.SetStateAction<Date | undefined>>;
  /**
   * Show the year dropdown, if true all fields must be selected to output the value
   */
  showDateOfBirthYear?: boolean;
  styles: DropdownStyles;
  errorMessage?: string;
};

export const DateOfBirthSelector: React.FC<DateOfBirthProps> = ({
  dateOfBirth,
  setDateOfBirth,
  showDateOfBirthYear,
  styles,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<string | undefined>(
    dateOfBirth ? String(dateOfBirth.getDate()) : undefined
  );
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(
    dateOfBirth ? String(dateOfBirth.getMonth() + 1) : undefined
  );
  const [selectedYear, setSelectedYear] = useState<string | undefined>(
    dateOfBirth ? String(dateOfBirth.getFullYear()) : undefined
  );

  const handleDayChange = (day: string) => {
    setSelectedDay(day);
    updateDateOfBirth(day, selectedMonth, selectedYear);
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateDateOfBirth(selectedDay, month, selectedYear);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    updateDateOfBirth(selectedDay, selectedMonth, year);
  };

  const updateDateOfBirth = (day?: string, month?: string, year?: string) => {
    if (day && month && (!showDateOfBirthYear || year)) {
      const newDate = new Date(
        parseInt(showDateOfBirthYear && year ? year : "1970"),
        parseInt(month) - 1,
        parseInt(day)
      );
      setDateOfBirth(newDate);
    }
  };

  return (
    <DateOfBirthContainer data-id="date-of-birth-container">
      <DateOfBirthLabel styles={styles}>{t("DateOfBirth")}</DateOfBirthLabel>
      <DropdownContainer>
        <Dropdown
          selected={selectedDay}
          options={generateDays()}
          onSelect={(selectedDay) => handleDayChange(selectedDay as string)}
          placeholder={t("Day")}
          styles={styles}
          isErrored={!!errorMessage && !selectedDay}
          dataId="date-of-birth-day"
        />
        <Dropdown
          selected={selectedMonth}
          options={generateMonths()}
          onSelect={(selectedMonth) =>
            handleMonthChange(selectedMonth as string)
          }
          placeholder={t("Month")}
          styles={styles}
          isErrored={!!errorMessage && !selectedMonth}
          dataId="date-of-birth-month"
        />
        {showDateOfBirthYear && (
          <Dropdown
            selected={selectedYear}
            options={generateYears()}
            onSelect={(selectedYear) =>
              handleYearChange(selectedYear as string)
            }
            placeholder={t("Year")}
            styles={styles}
            isErrored={!!errorMessage && !selectedYear}
            dataId="date-of-birth-year"
          />
        )}
      </DropdownContainer>
      {errorMessage && (
        <ErrorMessage styles={styles} data-id="date-of-birth-error">
          {errorMessage}
        </ErrorMessage>
      )}
    </DateOfBirthContainer>
  );
};

export default DateOfBirthSelector;
